<template>
  <div class="content">
    <div class="md-layout">
      <div
        class="md-layout-item md-small-size-50 md-xsmall-size-100 md-size-25"
        data-tour="application_card_tag"
      >
        <edit-app-tag-form
          v-bind:app="app"
          v-bind:ogApp="ogApp"
          header-color="primary"
        ></edit-app-tag-form>
      </div>
      <div
        class="md-layout-item md-small-size-50 md-xsmall-size-100 md-size-25"
        data-tour="application_card_id"
      >
        <app-id-form
          v-bind:app="app"
          v-bind:ogApp="ogApp"
          header-color="primary"
        ></app-id-form>
      </div>
      <div
        class="md-layout-item md-small-size-50 md-xsmall-size-100 md-size-25"
        data-tour="application_card_gateway_url"
      >
        <gateway-url-form
          v-bind:gwUrl="gatewayUrl"
          header-color="primary"
        ></gateway-url-form>
      </div>
      <div
        class="md-layout-item md-small-size-50 md-xsmall-size-100 md-size-25"
        data-tour="application_card_secret"
      >
        <app-shared-secret-form
          v-bind:app="app"
          v-bind:ogApp="ogApp"
          header-color="primary"
        ></app-shared-secret-form>
      </div>
      <div class="md-layout-item md-size-100">
        <webhooks
          ref="hookListing"
          v-bind:appId="id"
          v-bind:defaultChannelId="defaultChannelId"
          v-on:dirtyHooks="updateDirtyHooks"
          v-on:hooksAutoSave="updateConfig"
          v-on:updateHideSubmitSnackbar="updateHideSubmitSnackbar"
          header-color="primary"
        ></webhooks>
      </div>
      <div
        class="md-layout-item md-size-100"
        data-tour="application_card_channels"
      >
        <channel-listing
          ref="channelListing"
          v-bind:appId="id"
          v-on:dirtyChannels="updateDirtyChannels"
          header-color="primary"
        ></channel-listing>
      </div>
      <div
        class="md-layout-item md-size-100"
        data-tour="application_card_advanced"
      >
        <advanced-config
          ref="advancedConfig"
          v-bind:defaultChannel="defaultChannel"
          v-bind:defaultOgChannel="defaultOgChannel"
          header-color="primary"
          @validationErrorChanged="onAdvancedFormValidationErrorChanged"
        ></advanced-config>
      </div>
    </div>
    <submit-snackbar
      v-bind:validationError="validationError"
      v-bind:dirty="dirty"
      v-bind:errorsOccurred="submitErrorsOccurred"
      v-on:submit="updateConfig"
    ></submit-snackbar>
  </div>
</template>

<script>
import { get } from "vuex-pathify";
import _ from "lodash";
import AppModel from "@/models/application";
import api from "@/api";
import {
  EditAppTagForm,
  AppIdForm,
  AppSharedSecretForm,
  ChannelListing,
  GatewayUrlForm,
  AdvancedConfig
} from "@/pages";
import { Webhooks } from "@/fmComponents";
import { SubmitSnackbar } from "@/fmComponents";

export default {
  components: {
    EditAppTagForm,
    AppIdForm,
    GatewayUrlForm,
    AppSharedSecretForm,
    ChannelListing,
    Webhooks,
    SubmitSnackbar,
    AdvancedConfig,
  },
  data: () => ({
    dirtyHooks: false,
    dirtyChannels: false,
    hideSubmitSnackbar: false,
    defaultChannelId: null,
    isAdvancedFormError: false
  }),
  props: {
    id: null
  },
  watch: {
    defaultChannel: {
      deep: true,
      handler(newValue, oldValue) {
        this.checkForDirtyDefaultChannel();
      }
    },
    appChannels: function (newValue, oldValue) {
      // if appChannels contains & array with length (i.e. channels are loaded), search for the id of the default channel, if found return it, if not found {}.id returns undefined; otherwise return null
      this.defaultChannelId =
        Array.isArray(newValue) && newValue.length
          ? (newValue.find((record) => record.default == true) || {}).id
          : null;
    }
  },
  computed: {
    appChannels: get("channels/entities"),
    appOgChannels: get("channels/ogEntities"),
    defaultChannel: function () {
      // if appChannels contains & array with length (i.e. channels are loaded), return the default channel; otherwise return null
      return Array.isArray(this.appChannels) && this.appChannels.length
        ? this.appChannels.find((record) => record.default == true)
        : null;
    },
    defaultOgChannel: function () {
      // if appOgChannels contains & array with length (i.e. channels are loaded), return the default og channel; otherwise return null
      return Array.isArray(this.appOgChannels) && this.appOgChannels.length
        ? this.appOgChannels.find((record) => record.default == true)
        : null;
    },
    app: get("applications/loadedEntity"),
    ogApp: get("applications/loadedOgEntity"),
    dirty: function () {
      var dirtyApp = !_.isEqual(this.app, this.ogApp);
      return (
        !this.hideSubmitSnackbar &&
        (dirtyApp || this.dirtyHooks || this.dirtyChannels)
      );
    },
    submitErrorsOccurred: function () {
      return (
        get("applications/errorsOccurred") && get("webhooks/errorsOccurred")
      );
    },
    gatewayUrl: function () {
      return api.props.config.codepenGatewayUrl;
    },
    validationError: function() {
      return this.isAdvancedFormError;
    }
  },
  created() {
    this.$store.set(
      "webhooks/endPoint",
      api.props.endPoints.applicationWebhook
    );
    this.loadEntity();
  },
  mounted() {
    if (this.app.disabled) {
      this.$store.commit(
        "pushAlert",
        "This application is currently disabled. Applications require an active subscription to be used."
      );
    }
  },
  methods: {
    loadEntity: function () {
      var defaultEntity = new AppModel();
      defaultEntity.id = this.id;
      this.$store.dispatch("applications/loadEntity", defaultEntity);
    },
    updateDirtyHooks: function(isDirty) {
      this.dirtyHooks = isDirty;
    },
    checkForDirtyDefaultChannel: function () {
      if (!_.isEqual(this.defaultChannel, this.defaultOgChannel)) {
        this.updateDirtyChannels(true);
      } else {
        this.updateDirtyChannels(false);
      }
    },
    updateDirtyChannels: function(isDirty) {
      this.dirtyChannels = isDirty;
    },
    updateConfig: function () {
      if (this.validationError) {
        this.$store.commit("pushError", "Your change has one or more errors.");
        return;
      }
      var me = this;

      if (!_.isEqual(this.app, this.ogApp)) {
        this.$store
          .dispatch("applications/updateEntity", this.app)
          .then(() => {
            me.loadEntity();
          })
          .catch(() => {
            me.loadEntity();
          });
      }

      this.$refs.hookListing.updateConfig();
      this.$refs.channelListing.updateConfig();
    },
    // This functionality was added during UI change to show app hooks & default channel hooks at the same time.
    // This functionality was needed because the submit snackbar was showing up over the webhook modal, blocking the "apply" button on short screens
    updateHideSubmitSnackbar(newVal) {
      this.hideSubmitSnackbar = newVal == "hidden" ? true : false;
    },
    onAdvancedFormValidationErrorChanged(val) {
      this.isAdvancedFormError = val;
    }
  }
};
</script>